import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Plus, 
  Clock, 
  BookOpen, 
  Target,
  ChevronLeft,
  ChevronRight,
  Edit3,
  Trash2,
  CheckCircle,
  AlertCircle,
  Play
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { getStudentProfileByToken } from '../../services/elearningStudentService';
import { useTenantContext } from '../../context/TenantContext';
import { useToast } from '../../hooks/useToast';
import StudentLayout from '../../components/student/StudentLayout';

interface StudySession {
  id: string;
  title: string;
  description?: string;
  date: string;
  startTime: string;
  endTime: string;
  type: 'lesson' | 'quiz' | 'review' | 'practice';
  courseId?: string;
  courseName?: string;
  isCompleted: boolean;
  priority: 'low' | 'medium' | 'high';
}

const StudentCalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  
  const [studentProfile, setStudentProfile] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>('month');

  // Données des sessions d'étude simulées
  const [studySessions, setStudySessions] = useState<StudySession[]>([
    {
      id: '1',
      title: 'Cours JavaScript - Variables',
      description: 'Apprendre les bases des variables en JavaScript',
      date: '2024-01-27',
      startTime: '09:00',
      endTime: '10:30',
      type: 'lesson',
      courseId: 'js-101',
      courseName: 'JavaScript pour débutants',
      isCompleted: false,
      priority: 'high'
    },
    {
      id: '2',
      title: 'Quiz HTML/CSS',
      description: 'Test de connaissances sur HTML et CSS',
      date: '2024-01-27',
      startTime: '14:00',
      endTime: '15:00',
      type: 'quiz',
      courseId: 'web-101',
      courseName: 'Développement Web',
      isCompleted: true,
      priority: 'medium'
    },
    {
      id: '3',
      title: 'Révision React',
      description: 'Revoir les concepts de base de React',
      date: '2024-01-28',
      startTime: '10:00',
      endTime: '11:30',
      type: 'review',
      courseId: 'react-101',
      courseName: 'React Fondamentaux',
      isCompleted: false,
      priority: 'medium'
    },
    {
      id: '4',
      title: 'Exercices pratiques Python',
      description: 'Pratiquer les boucles et conditions',
      date: '2024-01-29',
      startTime: '16:00',
      endTime: '17:30',
      type: 'practice',
      courseId: 'python-101',
      courseName: 'Python pour débutants',
      isCompleted: false,
      priority: 'low'
    }
  ]);

  const sessionTypes = [
    { value: 'lesson', label: 'Leçon', icon: BookOpen, color: 'bg-blue-500' },
    { value: 'quiz', label: 'Quiz', icon: Target, color: 'bg-purple-500' },
    { value: 'review', label: 'Révision', icon: Clock, color: 'bg-orange-500' },
    { value: 'practice', label: 'Pratique', icon: Play, color: 'bg-green-500' }
  ];

  const priorities = [
    { value: 'low', label: 'Basse', color: 'border-gray-300' },
    { value: 'medium', label: 'Moyenne', color: 'border-yellow-400' },
    { value: 'high', label: 'Haute', color: 'border-red-400' }
  ];

  // Callback appelé quand le profil étudiant est chargé dans le layout
  const handleStudentProfileLoaded = (profile: any) => {
    setStudentProfile(profile);
    setIsLoading(false);
  };



  const handleToggleComplete = (sessionId: string) => {
    setStudySessions(sessions => 
      sessions.map(s => 
        s.id === sessionId 
          ? { ...s, isCompleted: !s.isCompleted }
          : s
      )
    );
    
    const session = studySessions.find(s => s.id === sessionId);
    if (session) {
      toast({
        title: session.isCompleted ? 'Session rouverte' : 'Session terminée',
        description: `"${session.title}" a été ${session.isCompleted ? 'rouverte' : 'marquée comme terminée'}`
      });
    }
  };

  const handleDeleteSession = (sessionId: string) => {
    setStudySessions(sessions => sessions.filter(s => s.id !== sessionId));
    toast({
      title: 'Session supprimée',
      description: 'La session d\'apprentissage a été supprimée'
    });
  };

  const getSessionsForDate = (date: string) => {
    return studySessions.filter(session => session.date === date);
  };

  const getTodaySessions = () => {
    const today = new Date().toISOString().split('T')[0];
    return getSessionsForDate(today);
  };

  const getUpcomingSessions = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    return studySessions
      .filter(session => new Date(session.date) >= tomorrow)
      .sort((a, b) => new Date(a.date + ' ' + a.startTime).getTime() - new Date(b.date + ' ' + b.startTime).getTime())
      .slice(0, 5);
  };

  const getTypeConfig = (type: StudySession['type']) => {
    return sessionTypes.find(t => t.value === type) || sessionTypes[0];
  };

  const getPriorityConfig = (priority: StudySession['priority']) => {
    return priorities.find(p => p.value === priority) || priorities[0];
  };

  const formatTime = (time: string) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };

  // Statistiques
  const totalSessions = studySessions.length;
  const completedSessions = studySessions.filter(s => s.isCompleted).length;
  const todaySessions = getTodaySessions();
  const upcomingSessions = getUpcomingSessions();



  return (
    <StudentLayout
      title="Mon Planning d'Étude"
      description="Organisez votre temps d'apprentissage et suivez vos sessions"
      onStudentProfileLoaded={handleStudentProfileLoaded}
      headerContent={
        <Button className="flex items-center space-x-2">
          <Plus className="h-4 w-4" />
          <span>Nouvelle session</span>
        </Button>
      }
    >
      <SEOMetaTags 
        title="Mon Planning - E-Learning"
        description="Organisez votre temps d'apprentissage et planifiez vos sessions d'étude."
        keywords="planning, calendrier, organisation, étude, e-learning"
      />

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total sessions</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalSessions}</p>
                  </div>
                  <Calendar className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Aujourd'hui</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{todaySessions.length}</p>
                  </div>
                  <Clock className="h-8 w-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Terminées</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{completedSessions}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Taux de réalisation</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {totalSessions > 0 ? Math.round((completedSessions / totalSessions) * 100) : 0}%
                    </p>
                  </div>
                  <Target className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Sessions d'aujourd'hui */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Clock className="h-5 w-5 text-orange-500" />
                    <span>Aujourd'hui</span>
                    <Badge variant="secondary">{todaySessions.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {todaySessions.length > 0 ? (
                    <div className="space-y-4">
                      {todaySessions.map((session, index) => {
                        const typeConfig = getTypeConfig(session.type);
                        const priorityConfig = getPriorityConfig(session.priority);
                        const Icon = typeConfig.icon;
                        
                        return (
                          <motion.div
                            key={session.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className={`p-4 rounded-lg border-l-4 ${priorityConfig.color} bg-white dark:bg-gray-800 shadow-sm`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start space-x-3">
                                <div className={`p-2 rounded-lg ${typeConfig.color} bg-opacity-20`}>
                                  <Icon className={`h-4 w-4 text-white`} />
                                </div>
                                <div className="flex-1">
                                  <h4 className="font-semibold text-gray-900 dark:text-white">
                                    {session.title}
                                  </h4>
                                  {session.description && (
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                      {session.description}
                                    </p>
                                  )}
                                  <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                                    <span>{formatTime(session.startTime)} - {formatTime(session.endTime)}</span>
                                    {session.courseName && (
                                      <span className="text-blue-600 dark:text-blue-400">
                                        {session.courseName}
                                      </span>
                                    )}
                                    <Badge variant="outline" className="text-xs">
                                      {typeConfig.label}
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-2">
                                {session.isCompleted && (
                                  <CheckCircle className="h-5 w-5 text-green-500" />
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleToggleComplete(session.id)}
                                >
                                  {session.isCompleted ? 'Rouvrir' : 'Terminer'}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteSession(session.id)}
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-400">
                        Aucune session prévue pour aujourd'hui
                      </p>
                      <Button className="mt-4" size="sm">
                        <Plus className="h-4 w-4 mr-2" />
                        Planifier une session
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sessions à venir */}
            <div>
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <AlertCircle className="h-5 w-5 text-blue-500" />
                    <span>Prochaines sessions</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {upcomingSessions.length > 0 ? (
                    <div className="space-y-3">
                      {upcomingSessions.map((session, index) => {
                        const typeConfig = getTypeConfig(session.type);
                        const Icon = typeConfig.icon;
                        
                        return (
                          <motion.div
                            key={session.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800"
                          >
                            <div className="flex items-start space-x-3">
                              <div className={`p-1.5 rounded ${typeConfig.color} bg-opacity-20`}>
                                <Icon className="h-3 w-3 text-white" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h5 className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                  {session.title}
                                </h5>
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                  {formatDate(session.date)}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {formatTime(session.startTime)} - {formatTime(session.endTime)}
                                </p>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-3" />
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Aucune session planifiée
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Raccourcis */}
              <Card className="border-0 shadow-lg mt-6">
                <CardHeader>
                  <CardTitle className="text-lg">Actions rapides</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button variant="outline" className="w-full justify-start" size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Nouvelle session
                  </Button>
                  <Button variant="outline" className="w-full justify-start" size="sm">
                    <Calendar className="h-4 w-4 mr-2" />
                    Vue calendrier
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start" 
                    size="sm"
                    onClick={() => navigate('/elearning/student/goals')}
                  >
                    <Target className="h-4 w-4 mr-2" />
                    Mes objectifs
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
    </StudentLayout>
  );
};

export default StudentCalendarPage;
